import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/app/auth/context/AuthContext';
import { useDebouncedValue } from '~/app/hooks/useDebouncedValue';
import {
  IncidentService,
  type IncidentListItem,
  type IncidentPaginationMeta,
} from '@/app/services/incident/incident.service';

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;

const emptyMeta: IncidentPaginationMeta = {
  total: 0,
  page: DEFAULT_PAGE,
  limit: DEFAULT_LIMIT,
  totalPages: 0,
};

type TablePagination = {
  current?: number;
  pageSize?: number;
};

export function useIncidentOverview() {
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const debouncedSearchValue = useDebouncedValue(searchValue.trim());

  useEffect(() => {
    setPage(DEFAULT_PAGE);
  }, [debouncedSearchValue]);

  const query = useQuery({
    queryKey: [
      'incidents',
      'overview',
      organizationId,
      page,
      limit,
      debouncedSearchValue,
    ],
    queryFn: () =>
      IncidentService.list({
        page,
        limit,
        q: debouncedSearchValue || undefined,
      }),
    enabled: !!organizationId,
  });

  const incidents = query.data?.data ?? [];
  const meta = query.data?.meta ?? emptyMeta;
  const pagination = useMemo(
    () => ({
      current: meta.page,
      pageSize: meta.limit,
      total: meta.total,
      showSizeChanger: true,
    }),
    [meta.limit, meta.page, meta.total],
  );

  const handleSearchChange = (nextValue: string): void => {
    setSearchValue(nextValue);
    setPage(DEFAULT_PAGE);
  };

  const handleTableChange = (nextPagination: TablePagination): void => {
    setPage(nextPagination.current ?? DEFAULT_PAGE);
    setLimit(nextPagination.pageSize ?? DEFAULT_LIMIT);
  };

  return {
    incidents,
    error: (query.error ?? null) as Error | null,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    pagination,
    searchValue,
    handleSearchChange,
    handleTableChange,
    refetch: query.refetch,
  } satisfies {
    incidents: IncidentListItem[];
    error: Error | null;
    isFetching: boolean;
    isLoading: boolean;
    pagination: {
      current: number;
      pageSize: number;
      total: number;
      showSizeChanger: boolean;
    };
    searchValue: string;
    handleSearchChange: (nextValue: string) => void;
    handleTableChange: (nextPagination: TablePagination) => void;
    refetch: () => Promise<unknown>;
  };
}
